import { FunctionComponent as FC, useEffect, useRef, useState } from "react"
import { Card, Typography, Spin } from "antd"
import { useTheme } from "styled-components"
import { useOutletContext } from "react-router-dom"
import { Config } from "vega-lite"
import { VisualizationSpec } from "react-vega"
import { Chart } from "src/global/ui/Chart/Chart"
import { ChartConfig, vegaLiteSchema } from "src/global/ui/Chart/ChartConfig"
import { DailyDataHealthScore } from "src/api/graphql/graphql"
import { decimalToPercentageNumber } from "src/global/utils/formatNumber"
import { graphql } from "src/api/graphql/gql"
import { getDqiLabel } from "src/pages/DataHealthDashboard/utils/getDqiLabel"
import { unmaskFragment } from "src/api/graphql/fragment-masking"
import type { DataHealthDashboardOutletContext } from "src/pages/DataHealthDashboard/DataHealthDashboard"

export const DataHealthChartFragment = graphql(`
  fragment DataHealthChart on DataHealth {
    dailyScores {
      date
      score
      successfulExpectations
      validatedExpectations
    }
  }
`)

const { Text } = Typography

const DEFAULT_CHART_WIDTH = 600
const MIN_CHART_WIDTH = 300

interface ProcessedDataPoint {
  index: number
  date: string
  formattedDate: string
  score: number
  scorePercent: string
  successfulExpectations: number
  validatedExpectations: number
}

interface ChartData {
  values: ProcessedDataPoint[]
}

const processDataForChart = (dailyScores: DailyDataHealthScore[]): ProcessedDataPoint[] => {
  if (!dailyScores || dailyScores.length === 0) {
    return []
  }

  return dailyScores.map((item, index) => {
    const date = new Date(item.date)
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })

    const scoreValue = decimalToPercentageNumber(item.score, 1)

    return {
      index,
      date: item.date,
      formattedDate,
      score: scoreValue,
      scorePercent: `${scoreValue}%`,
      successfulExpectations: item.successfulExpectations,
      validatedExpectations: item.validatedExpectations,
    }
  })
}

const getXAxisValues = (chartData: ChartData): number[] => {
  return chartData.values.map((_, i) => i)
}

const createLabelExpression = (chartData: ChartData): string => {
  if (chartData.values.length === 0) return "''"

  const conditions = []

  const firstDate = new Date(chartData.values[0]?.date || "")
  const firstDateLabel = firstDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  conditions.push(`datum.value == 0 ? '${firstDateLabel}'`)

  // Show dates every 5 days (for 30 days total: 0, 5, 10, 15, 20, 25, 29)
  for (let i = 5; i < chartData.values.length - 1; i += 5) {
    if (chartData.values[i]) {
      const date = new Date(chartData.values[i].date)
      const dateLabel = date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
      conditions.push(`datum.value == ${i} ? '${dateLabel}'`)
    }
  }

  // Always show last date
  const lastIndex = chartData.values.length - 1
  const lastDate = new Date(chartData.values[lastIndex]?.date || "")
  const lastDateLabel = lastDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  conditions.push(`datum.value == ${lastIndex} ? '${lastDateLabel}'`)

  // Add the final else condition
  conditions.push("''")

  return conditions.join(" : ")
}

const createTickSizeExpression = (chartData: ChartData): string => {
  const conditions = []

  conditions.push(`datum.value == 0 ? 5`)
  conditions.push(`datum.value == ${chartData.values.length - 1} ? 5`)

  // 5-day intervals get size 4
  for (let i = 5; i < chartData.values.length - 1; i += 5) {
    conditions.push(`datum.value == ${i} ? 4`)
  }

  // Everything else gets size 3
  conditions.push("3")

  return conditions.join(" : ")
}

const getChartTitle = (dqi: string): string => {
  const dqiLabel = getDqiLabel(dqi)

  if (dqiLabel !== "Overall") {
    return `Daily ${dqiLabel} Health`
  }
  return "Daily Data Health"
}

const getTooltipLabels = (dqi: string) => {
  const dqiLabel = getDqiLabel(dqi)

  if (dqiLabel !== "Overall") {
    return {
      healthLabel: `${dqiLabel} Health`,
      validatedLabel: `${dqiLabel} Expectations validated`,
      successfulLabel: `Successful ${dqiLabel.toLowerCase()} Expectations`,
    }
  }

  return {
    healthLabel: "Data Health",
    validatedLabel: "Expectations validated",
    successfulLabel: "Successful Expectations",
  }
}

export const DataHealthChart: FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CHART_WIDTH)
  const theme = useTheme()
  const { dataHealth, dataHealthLoading, dqi } = useOutletContext<DataHealthDashboardOutletContext>()

  const dataHealthChart = unmaskFragment(DataHealthChartFragment, dataHealth)
  const dailyScores = dataHealthChart?.dailyScores
  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth
        const paddingOffset = parseInt(theme.spacing.l) + parseInt(theme.spacing.xs)
        setContainerWidth(Math.max(MIN_CHART_WIDTH, width - paddingOffset))
      }
    }

    measureWidth()

    window.addEventListener("resize", measureWidth)
    return () => window.removeEventListener("resize", measureWidth)
  }, [theme.spacing])

  const config: Config = ChartConfig({
    axis: {
      labelFontSize: 10,
      titleFontSize: 12,
      titleFontWeight: "lighter",
    },
  })

  const scoresData = processDataForChart(dailyScores || [])

  const chartData = { values: scoresData }

  const xAxisValues = getXAxisValues(chartData)
  const labelExpression = createLabelExpression(chartData)
  const tickSizeExpression = createTickSizeExpression(chartData)

  const chartTitle = getChartTitle(dqi)
  const tooltipLabels = getTooltipLabels(dqi)

  const hasData = !dataHealthLoading && dailyScores && dailyScores.length > 0
  const showEmptyState = !dataHealthLoading && (!dailyScores || dailyScores.length === 0)

  const spec: VisualizationSpec = {
    $schema: vegaLiteSchema,
    width: containerWidth,
    height: 300,
    padding: {
      left: parseInt(theme.spacing.xxs),
      right: parseInt(theme.spacing.xxs),
      bottom: parseInt(theme.spacing.xxs),
      top: parseInt(theme.spacing.xxs),
    },
    autosize: {
      type: "fit-x",
      resize: true,
    },
    config: config,
    data: chartData,
    encoding: {
      x: {
        field: "index",
        type: "quantitative",
        axis: {
          title: "Date",
          values: xAxisValues,
          labelExpr: labelExpression,
          tickSize: { expr: tickSizeExpression },
          grid: true,
          labelColor: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
          gridColor: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
          gridOpacity: 0.3,
          domainColor: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
        },
      },
      y: {
        field: "score",
        type: "quantitative",
        axis: {
          title: "% of distinct Expectations that succeeded",
          format: ".0f",
          values: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
          labelExpr: "datum.value + '%'",
          labelColor: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
          gridColor: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
          gridOpacity: 0.3,
          domainColor: theme.colors.neutralColorPalette.whites.colorTextTertiaryDark,
          tickColor: theme.colors.neutralColorPalette.whites.colorTextTertiaryDark,
        },
        scale: {
          domain: [0, 100],
        },
      },
      tooltip: [
        {
          field: "formattedDate",
          type: "nominal",
          title: "Date",
        },
        {
          field: "scorePercent",
          type: "nominal",
          title: tooltipLabels.healthLabel,
        },
        {
          field: "validatedExpectations",
          type: "quantitative",
          title: tooltipLabels.validatedLabel,
        },
        {
          field: "successfulExpectations",
          type: "quantitative",
          title: tooltipLabels.successfulLabel,
        },
      ],
    },
    layer: [
      {
        mark: { type: "line", color: theme.colors.info, strokeWidth: 2 },
      },
      {
        params: [
          {
            name: "hover",
            select: { type: "point", on: "pointerover", nearest: true, clear: "pointerout" },
          },
        ],
        mark: {
          type: "point",
          stroke: theme.colors.neutralColorPalette.whites.white,
          strokeWidth: 1,
          color: theme.colors.info,
        },
        encoding: {
          opacity: {
            condition: { param: "hover", empty: false, value: 1 },
            value: 0,
          },
        },
      },
    ],
  }

  return (
    <Card
      style={{
        border: `1px solid ${theme.colors.neutralColorPalette.blacks.colorFill}`,
        borderRadius: theme.radius.default,
        gap: theme.spacing.xxs,
        paddingBottom: theme.spacing.s,
      }}
    >
      <div style={{ marginBottom: theme.spacing.xs }}>
        <Text
          strong
          style={{
            fontSize: theme.typography.fontSize.small,
            lineHeight: theme.typography.lineHeight.medium,
          }}
        >
          {chartTitle}
        </Text>
        <br />
        <Text
          style={{
            fontSize: theme.typography.fontSize.small,
            lineHeight: theme.typography.lineHeight.medium,
            color: theme.colors.neutralColorPalette.blacks.colorTextTertiary,
          }}
        >
          Last 30 days (UTC)
        </Text>
      </div>
      <div ref={containerRef} style={{ height: 325, width: "100%" }}>
        {dataHealthLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        {hasData && <Chart spec={spec} />}
        {showEmptyState && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: theme.colors.neutralColorPalette.blacks.colorTextSecondary,
              fontSize: theme.typography.fontSize.medium,
            }}
          >
            Data Health metrics will be available after your first validation completes.
          </div>
        )}
      </div>
    </Card>
  )
}
